import React, { useState, useEffect } from "react";
import $ from "jquery";
import { toast } from "react-toastify";

const ALL_LANGUAGES = [
  { code: "EN", displayName: "English", englishName: "English" },
  { code: "AR", displayName: "العربية", englishName: "Arabic" },
  { code: "FR", displayName: "Français", englishName: "French" },
  { code: "DE", displayName: "Deutsch", englishName: "German" },
  { code: "ES", displayName: "Español", englishName: "Spanish" },
];

function authHeaders(extra) {
  return Object.assign(
    { Authorization: "Bearer " + (localStorage.getItem("access_token") || "") },
    extra || {}
  );
}

export default function LanguageSelectionTab({ procedureId, activeTab }) {
  // State: selectedLanguages, savedLanguages, saving, languageApprovalSummary, approvalStatusLoaded, loading
  const [selectedLanguages, setSelectedLanguages] = useState([]); // Local UI state (unsaved changes)
  const [savedLanguages, setSavedLanguages] = useState([]); // Actual saved languages from DB
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [languageApprovalSummary, setLanguageApprovalSummary] = useState({});
  const [approvalStatusLoaded, setApprovalStatusLoaded] = useState(false);
  const [approvalStatus, setApprovalStatus] = useState({});

  // Load languages on mount and when activeTab changes
  useEffect(() => {
    if (procedureId && activeTab === "languages") {
      loadLanguages();
    }
  }, [procedureId, activeTab]);

  // Refetch approval statuses when activeTab changes
  useEffect(() => {
    if (procedureId && activeTab === "languages") {
      loadProcedureApprovalStatuses();
    }
  }, [activeTab]);

  const loadLanguages = async () => {
    if (!procedureId) {
      console.warn("No procedureId provided");
      setLoading(false);
      return;
    }

    try {
      console.log("[LOAD-LANGUAGES] Fetching languages from API");

      const response = await $.ajax({
        url: `/my-jeeva-api/procedure/${procedureId}/languages`,
        type: "GET",
        dataType: "json",
        headers: authHeaders(),
      });

      const responseArray = response && response.result ? response.result : [];
      console.log("[LOAD-LANGUAGES] API Response:", responseArray);

      // Get enabled languages (excluding EN)
      const enabledCodes = responseArray
        .filter((lang) => lang.isEnabled === true || lang.isEnabled === "true")
        .map((lang) => lang.code.toUpperCase());

      console.log("[LOAD-LANGUAGES] Enabled languages:", enabledCodes);

      setSelectedLanguages(enabledCodes); // For UI changes
      setSavedLanguages(enabledCodes);    // For DB truth

      // STEP 2: After getting languages, fetch approval status for EACH enabled language
      console.log("[LOAD-LANGUAGES] Now fetching approval status for each enabled language...");
      await loadProcedureApprovalStatuses(enabledCodes);

      setLoading(false);
    } catch (err) {
      console.error("Error loading languages:", err);
      toast.error("Failed to load languages");
      setLoading(false);
    }
  };

  const loadProcedureApprovalStatuses = async (enabledLanguages = null) => {
    if (!procedureId) {
      console.warn("No procedureId provided");
      return;
    }

    try {
      console.log(`[LOAD-PROCEDURE-STATUS] Fetching approval statuses for procedure: ${procedureId}`);

      // Use passed languages or fallback to savedLanguages
      const languagesForApproval = enabledLanguages || savedLanguages;

      // Exclude EN - English doesn't need approval
      const nonEnglishLanguages = languagesForApproval.filter(lang => lang !== "EN");

      console.log(`[LOAD-PROCEDURE-STATUS] Languages to check (excluding EN):`, nonEnglishLanguages);

      // Call API for EACH enabled language separately
      const approvalStatusPerLanguage = {};

      // Sequential API calls for each language
      for (const langCode of nonEnglishLanguages) {
        try {
          console.log(`[LOAD-PROCEDURE-STATUS] Calling API for language: ${langCode}`);

          const response = await $.ajax({
            url: `/my-jeeva-api/translation-approval/procedure/${procedureId}/approval-status`,
            type: "GET",
            dataType: "json",
            headers: authHeaders(),
            data: {
              languageCode: langCode
            }
          });

          if (response && response.success && response.result) {
            console.log(`[LOAD-PROCEDURE-STATUS] Response for ${langCode}:`, response.result);
            // Store per-language approval status
            approvalStatusPerLanguage[langCode] = response.result;
          } else {
            console.warn(`[LOAD-PROCEDURE-STATUS] No result for ${langCode}:`, response);
            approvalStatusPerLanguage[langCode] = { overallStatus: "NO_CONTENT", categoryStatuses: {} };
          }
        } catch (err) {
          console.error(`[LOAD-PROCEDURE-STATUS] Error fetching status for ${langCode}:`, err);
          approvalStatusPerLanguage[langCode] = { overallStatus: "ERROR", categoryStatuses: {} };
        }
      }

      console.log(`[LOAD-PROCEDURE-STATUS] All approval statuses per language:`, approvalStatusPerLanguage);

      // Store all per-language statuses and update badges
      setApprovalStatus(approvalStatusPerLanguage);
      setApprovalStatusLoaded(true);

      console.log(`[LOAD-PROCEDURE-STATUS] Approval statuses loaded successfully.`);
    } catch (err) {
      console.error(`[LOAD-PROCEDURE-STATUS] Error in loadProcedureApprovalStatuses:`, err);
      setApprovalStatusLoaded(true);
    }
  };

  const handleLanguageToggle = (code) => {
    if (code === "EN") return;
    setSelectedLanguages((prev) =>
      prev.includes(code) ? prev.filter((l) => l !== code) : [...prev, code]
    );
  };

  const handleSaveLanguages = async () => {
    try {
      setSaving(true);
      const enabledLanguages = ["EN", ...selectedLanguages];

      // Detect deselected languages
      const deselectedLanguages = savedLanguages.filter(lang => !selectedLanguages.includes(lang) && lang !== "EN");
      console.log("[SAVE-LANGUAGES] Deselected languages:", deselectedLanguages);

      await $.ajax({
        url: `/my-jeeva-api/procedure/${procedureId}/languages`,
        type: "PUT",
        dataType: "json",
        contentType: "application/json",
        headers: authHeaders(),
        data: JSON.stringify({ enabledLanguages }),
      });

      // Delete approval statuses for deselected languages
      if (deselectedLanguages.length > 0) {
        console.log("[SAVE-LANGUAGES] Deleting approval statuses for deselected languages...");
        for (const lang of deselectedLanguages) {
          try {
            await $.ajax({
              url: `/my-jeeva-api/translation-approval/delete-by-language`,
              type: "DELETE",
              dataType: "json",
              contentType: "application/json",
              headers: authHeaders(),
              data: JSON.stringify({
                procedureId: procedureId,
                languageCode: lang
              }),
            });
            console.log(`[SAVE-LANGUAGES] Deleted approval statuses for language: ${lang}`);
          } catch (err) {
            console.error(`[SAVE-LANGUAGES] Error deleting approval statuses for ${lang}:`, err);
          }
        }
      }

      // Fetch fresh approval statuses from API to ensure data is up-to-date
      console.log("[SAVE-LANGUAGES] Fetching fresh approval statuses after language change...");
      await loadProcedureApprovalStatuses(selectedLanguages);

      // Update savedLanguages AFTER approval statuses are fetched
      setSavedLanguages(selectedLanguages);

      toast.dismiss();
      toast(<b><img src="assets/images/done.svg" style={{ paddingRight: '8px', height: '20px', width: '20px' }} content="" />&nbsp;&nbsp;Languages configured successfully</b>, {
        position: "top-center",
      });

      setSaving(false);
    } catch (err) {
      console.error("Error saving languages:", err);
      toast.error("Failed to save languages");
      setSaving(false);
    }
  };

  const loadLanguageApprovalSummary = () => {
    const summary = {};

    console.log("[SUMMARY] Current approvalStatus:", approvalStatus);
    console.log("[SUMMARY] Current savedLanguages:", savedLanguages);

    // For each enabled language, get its approval status
    for (const lang of savedLanguages.filter(l => l !== "EN")) {
      const langStatus = approvalStatus[lang];  // Get this language's status from the object

      if (!langStatus || !langStatus.categoryStatuses) {
        console.log(`[SUMMARY] No status found for ${lang}`);
        summary[lang] = { pendingCount: 0, pendingCategories: [] };
        continue;
      }

      const pendingCategories = [];
      const categoryStatuses = langStatus.categoryStatuses;

      console.log(`[SUMMARY] Checking ${lang} - categoryStatuses:`, categoryStatuses);

      // Check each category - if it exists but not approved, it's pending
      if (categoryStatuses.library && categoryStatuses.library.exists && !categoryStatuses.library.fullyApproved) {
        pendingCategories.push("Library");
      }
      if (categoryStatuses.journals && categoryStatuses.journals.exists && !categoryStatuses.journals.fullyApproved) {
        pendingCategories.push("Journal");
      }
      if (categoryStatuses.riskRules && categoryStatuses.riskRules.exists && !categoryStatuses.riskRules.fullyApproved) {
        pendingCategories.push("Risk Rule");
      }
      if (categoryStatuses.surveys && categoryStatuses.surveys.exists && !categoryStatuses.surveys.fullyApproved) {
        pendingCategories.push("Survey");
      }
      if (categoryStatuses.standaloneReminders && categoryStatuses.standaloneReminders.exists && !categoryStatuses.standaloneReminders.fullyApproved) {
        pendingCategories.push("Stand-alone Reminder");
      }

      // If overall status is NO_CONTENT, no pending categories
      if (langStatus.overallStatus === "NO_CONTENT") {
        pendingCategories.length = 0;
      }

      const pendingCount = pendingCategories.length;
      summary[lang] = { pendingCount, pendingCategories };
      console.log(`[SUMMARY-${lang}] Pending count: ${pendingCount}, categories:`, pendingCategories);
    }

    setLanguageApprovalSummary(summary);
  };

  // Update language approval summary when approval status changes
  useEffect(() => {
    loadLanguageApprovalSummary();
  }, [approvalStatus, savedLanguages]);

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", paddingTop: "24px" }}>
        <p style={{ fontSize: "14px", color: "#999" }}>⏳ Loading enabled languages...</p>
      </div>
    );
  }

  return (
    <div style={{ marginLeft: "1vw" }}>
      <p style={{ color: "#666", fontSize: "14px", marginBottom: "16px", fontWeight: "500" }}>
        Select which languages this treatment should be available in
      </p>
      <div style={{ maxWidth: "600px", marginLeft: "0" }}>
        {ALL_LANGUAGES.map((lang) => {
          const summary = languageApprovalSummary[lang.code] || { pendingCount: 0, pendingCategories: [] };
          const hasPending = summary.pendingCount > 0;

          return (
            <div
              key={lang.code}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "10px 12px",
                border: "1px solid #e0e0e0",
                borderRadius: "0",
                marginBottom: "8px",
                cursor: lang.code === "EN" ? "default" : "pointer",
                transition: "background 0.2s ease",
                background: selectedLanguages.includes(lang.code) || lang.code === "EN" ? "#f5f5f5" : "#fff",
                opacity: lang.code === "EN" ? 0.7 : 1,
              }}
              onClick={() => {
                if (lang.code !== "EN") handleLanguageToggle(lang.code);
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <input
                  type="checkbox"
                  checked={selectedLanguages.includes(lang.code) || lang.code === "EN"}
                  onChange={() => {}}
                  disabled={lang.code === "EN"}
                  style={{ cursor: lang.code === "EN" ? "not-allowed" : "pointer" }}
                />
                <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  <strong style={{ fontWeight: "600" }}>{lang.code === "EN" ? lang.displayName : `${lang.englishName} (${lang.displayName})`}</strong>
                  {lang.code === "EN" && <span style={{ fontSize: "11px", color: "#999", marginLeft: "6px" }}>(Base Language)</span>}
                </span>
              </div>
              {lang.code !== "EN" && approvalStatusLoaded && hasPending && savedLanguages.includes(lang.code) && (
                <div
                  style={{
                    padding: "4px 10px",
                    background: "#f44336",
                    color: "#fff",
                    fontSize: "11px",
                    fontWeight: "600",
                    borderRadius: "3px",
                    whiteSpace: "nowrap",
                    marginLeft: "8px",
                    title: `Pending approvals: ${summary.pendingCategories.join(", ")}`
                  }}
                  title={`Pending approvals: ${summary.pendingCategories.join(", ")}`}
                >
                  ⚠ {summary.pendingCount} Pending Approvals
                </div>
              )}
              {lang.code !== "EN" && approvalStatusLoaded && !hasPending && savedLanguages.includes(lang.code) && (
                <div
                  style={{
                    padding: "4px 10px",
                    background: "#4caf50",
                    color: "#fff",
                    fontSize: "11px",
                    fontWeight: "600",
                    borderRadius: "3px",
                    whiteSpace: "nowrap",
                    marginLeft: "8px"
                  }}
                >
                  ✓ Approvals Completed
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "20px", paddingTop: "12px", borderTop: "1px solid #e0e0e0" }}>
        <button
          style={{
            padding: "8px 16px",
            borderRadius: "0",
            border: "1px solid #282a65",
            fontSize: "13px",
            fontWeight: "600",
            cursor: "pointer",
            background: "#282a65",
            color: "#fff",
            transition: "background 0.2s ease",
          }}
          onClick={handleSaveLanguages}
          onMouseEnter={(e) => (e.target.style.background = "#1f1f45")}
          onMouseLeave={(e) => (e.target.style.background = "#282a65")}
          disabled={saving}
        >
          {saving ? "Saving..." : "Save Languages"}
        </button>
      </div>
    </div>
  );
}



// WEBPACK FOOTER //
// src/my-jeeva/projectmgmt/LanguageSelectionTab.js